import { useState } from 'react';
import '../../../../styles/kanbans/ai-tools/ai-tile.css';

interface AICounter {
  status: 'created' | 'launched' | 'completed' | 'failed';
  count: number;
}

interface AITileProps {
  title: string;
  description: string;
  actionLabel: string;
  isLoading?: boolean;
  onActionClick?: () => void;
  statusCounters?: AICounter[];
  taskType?: string;
  isHighlighted?: boolean;
}

export default function AITile({
  title,
  description,
  actionLabel,
  isLoading = false,
  onActionClick,
  statusCounters = [],
  taskType,
  isHighlighted = false
}: AITileProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const getStatusColor = (status: string): string => {
    if (status === 'completed') return 'green';
    if (status === 'launched' || status === 'created') return 'yellow';
    if (status === 'failed') return 'red';
    return 'gray';
  };

  const getStatusLabel = (status: string): string => {
    if (status === 'completed') return 'Completed';
    if (status === 'launched') return 'Launched';
    if (status === 'created') return 'Created';
    if (status === 'failed') return 'Failed';
    return status;
  };

  const getStatusTooltipText = (status: string, count: number): string => {
    if (status === 'completed') {
      const taskWord = count === 1 ? 'task' : 'tasks';
      return `You have ${count} completed ${taskWord}. Check documents tab to see results in newly created doc.`;
    }
    if (status === 'launched' || status === 'created') {
      const taskWord = count === 1 ? 'task' : 'tasks';
      return `You have ${count} ${taskWord} in progress. Results will appear soon in documents tab.`;
    }
    if (status === 'failed') {
      const taskWord = count === 1 ? 'task' : 'tasks';
      return `Failed: ${count} ${taskWord}`;
    }
    return `${getStatusLabel(status)}: ${count}`;
  };

  const activeCounters = statusCounters.filter(counter => counter.count > 0);

  return (
    <div
      className={`ai-tile ${isHighlighted ? 'ai-tile--highlighted' : ''}`}
      data-task-type={taskType}
    >
      <div className="ai-tile__header">
        <div className="ai-tile__title">{title}</div>
        {activeCounters.length > 0 && (
          <>
            <div className="ai-tile__status-indicators">
              {activeCounters.map((counter, index) => (
                <div
                  key={index}
                  className={`ai-tile__status-indicator ai-tile__status-indicator--${getStatusColor(counter.status)}`}
                  title={getStatusTooltipText(counter.status, counter.count)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              ))}
            </div>
            {activeCounters.map((counter, index) => (
              <span
                key={`tooltip-${index}`}
                className={`ai-tile__status-tooltip ${hoveredIndex === index ? 'ai-tile__status-tooltip--visible' : ''}`}
              >
                {getStatusTooltipText(counter.status, counter.count)}
              </span>
            ))}
          </>
        )}
      </div>
      <div className="ai-tile__description">{description}</div>
      <button 
        className="ai-tile__action"
        onClick={onActionClick}
        type="button"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="ai-tile__loader"></span>
            Generating...
          </>
        ) : (
          <>
            {actionLabel}
            <span className="ai-tile__action-arrow">→</span>
          </>
        )}
      </button>
    </div>
  );
}

